import { z } from 'zod';
import { isoDateTimeSchema } from './common.js';
export const transcodingWorkerSchema = z
    .object({
    worker_id: z.string().min(1),
    last_heartbeat_at: isoDateTimeSchema,
    alive: z.boolean(),
})
    .strict();
export const transcodingCountsSchema = z
    .object({
    queued: z.number().int().min(0),
    running: z.number().int().min(0),
    failed: z.number().int().min(0),
    done: z.number().int().min(0),
})
    .strict();
export const transcodingSummaryResponseSchema = z
    .object({
    counts: transcodingCountsSchema,
    oldest_queued_age_sec: z.number().min(0).nullable(),
    stale_running: z.number().int().min(0),
    last_finished_at: isoDateTimeSchema.nullable(),
    workers: z.array(transcodingWorkerSchema),
})
    .strict();
export const transcodingRequeueResponseSchema = z
    .object({
    requeued: z.number().int().min(0),
})
    .strict();
/**
 * «Транскодинг» → «Перепрогон каталога» — STORY-095, track-transitions
 * arch §8.1. One body for all three routes:
 *
 *   GET  /admin/transcoding/backfill        admin, super_admin
 *   POST /admin/transcoding/backfill/start  super_admin only, else 403
 *   POST /admin/transcoding/backfill/pause  super_admin only, else 403
 *
 * Neither POST takes a body. Both are idempotent and answer 200 with the read
 * after the press; a press that changes nothing records nothing in the
 * journal. A press that changes something records `track_backfill.started` /
 * `track_backfill.paused` with the actor.
 *
 * `state`:
 *   - `running` / `paused` — `content.track_backfill_control.enabled`;
 *   - `no_worker` — no worker heartbeat younger than 300 s carries its
 *     processing settings, so what is left cannot be counted: `mode` and every
 *     count are `null` — unknown, never zero. The block then shows neither
 *     «Запустить» nor «Пауза» (coordinator, 2026-10-19): the body does not say
 *     whether the re-processing is on, and the start's confirmation cannot
 *     name a mode. There is deliberately no separate `enabled` field.
 * `mode` — what a pass does under the worker's settings: `analyse` while
 * trimming is off («только анализ»), `reprocess` while it is on («обрезанные
 * копии»).
 * Counts — the published catalogue under the settings in force
 * (`content.track_backfill_counts`): `done`, `left` (queued or not started),
 * `in_progress`, `failed`.
 * `last_finished_at` — when the latest catalogue job ended, done or failed;
 * `null` before the first.
 */
export const transcodingBackfillModeSchema = z.enum(['analyse', 'reprocess']);
const backfillCountSchema = z.number().int().min(0);
const transcodingBackfillKnownSchema = z
    .object({
    state: z.enum(['running', 'paused']),
    mode: transcodingBackfillModeSchema,
    done: backfillCountSchema,
    left: backfillCountSchema,
    in_progress: backfillCountSchema,
    failed: backfillCountSchema,
    last_finished_at: isoDateTimeSchema.nullable(),
})
    .strict();
const transcodingBackfillNoWorkerSchema = z
    .object({
    state: z.literal('no_worker'),
    mode: z.null(),
    done: z.null(),
    left: z.null(),
    in_progress: z.null(),
    failed: z.null(),
    last_finished_at: isoDateTimeSchema.nullable(),
})
    .strict();
export const transcodingBackfillResponseSchema = z.discriminatedUnion('state', [
    transcodingBackfillKnownSchema,
    transcodingBackfillNoWorkerSchema,
]);
/**
 * The payload of the `transcoding_pickup` notification a start sends right
 * after its switch commits — only a start that switched the re-processing on,
 * never a repeated one or a pause — so the worker runs its backfill planner at
 * once instead of at the next 10-minute tick (arch §5.3, §8.1). A worker that
 * does not know it claims as on any other notification.
 */
export const TRACK_BACKFILL_STARTED_NOTIFY_PAYLOAD = 'track_backfill_started';
//# sourceMappingURL=transcoding.js.map